import { Icon } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useSound } from './useSound'
import styles from './SoundToggle.module.css'

interface SoundToggleProps {
  className?: string
}

/**
 * The switch that turns the sound kit on and off.
 *
 * This is not optional decoration. A site that makes noise owes the visitor
 * a control that is visible without hunting — in the navigation rail, next
 * to everything else they might want — and a choice that is remembered.
 *
 * `data-silent` keeps the delegated hover and click cues off it, so the only
 * sound the button makes is its own confirmation tone.
 *
 * It renders nothing at all where Web Audio is unavailable, rather than
 * offering a switch that controls nothing.
 */
export const SoundToggle = ({ className }: SoundToggleProps) => {
  const { enabled, supported, toggle } = useSound()

  if (!supported) return null

  return (
    <button
      type="button"
      data-silent
      onClick={toggle}
      aria-pressed={enabled}
      aria-label={enabled ? 'Turn sound off' : 'Turn sound on'}
      className={cn(styles.toggle, enabled && styles.toggleOn, className)}
    >
      <Icon name={enabled ? 'speakerHigh' : 'speakerSlash'} size={20} />
      {enabled ? (
        <span className={styles.bars} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      ) : null}
    </button>
  )
}
